import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Brain, Wine, Heart, Zap, ArrowRight, Clock, User } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  { id: "all", label: "All Articles", icon: BookOpen },
  { id: "psychiatry", label: "Psychiatric Disorders", icon: Brain },
  { id: "deaddiction", label: "Drug Addiction", icon: Wine },
  { id: "sexual_health", label: "Sexual Health", icon: Heart },
  { id: "overlap", label: "The Overlap Zone", icon: Zap },
];

const PILLAR_COLORS = {
  psychiatry: "bg-primary/10 text-primary",
  deaddiction: "bg-orange-100 text-orange-700",
  sexual_health: "bg-pink-100 text-pink-700",
  overlap: "bg-accent/10 text-accent",
};

const PILLAR_LABELS = {
  psychiatry: "Psychiatric Disorders",
  deaddiction: "Drug Addiction",
  sexual_health: "Sexual Health",
  overlap: "The Overlap Zone",
};

export default function ResourcesPage() {
  const [articles, setArticles] = useState([]);
  const [activePillar, setActivePillar] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = activePillar === "all" ? `${API}/articles` : `${API}/articles?pillar=${activePillar}`;
    axios.get(url).then(res => {
      setArticles(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [activePillar]);

  return (
    <div>
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-6 h-6 text-accent" />
            <span className="text-sm text-accent font-medium uppercase tracking-wider">Resources</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="resources-heading">
            Expert Insights & Articles
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            In-depth articles by Dr. Ambrish Singal on psychiatric disorders, addiction recovery, sexual health, and the connections between them. Written for patients, families, and anyone seeking to understand.
          </p>
        </div>
      </section>

      {/* Pillar Tabs */}
      <section className="py-8 border-b sticky top-16 lg:top-20 bg-background/95 backdrop-blur-sm z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {PILLARS.map(p => (
              <button
                key={p.id}
                data-testid={`pillar-${p.id}`}
                onClick={() => setActivePillar(p.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                  activePillar === p.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-muted-foreground border-border hover:border-primary/40"
                }`}
              >
                <p.icon className="w-4 h-4" />
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16 text-muted-foreground">Loading articles...</div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">No articles found in this category.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map(article => (
                <Link key={article.slug} to={`/resources/${article.slug}`} data-testid={`article-${article.slug}`}>
                  <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer border group">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${PILLAR_COLORS[article.pillar] || "bg-secondary text-secondary-foreground"}`}>
                          {PILLAR_LABELS[article.pillar] || article.pillar}
                        </span>
                      </div>
                      <h3 className="font-heading font-semibold text-base leading-snug mb-3 group-hover:text-primary transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{article.author}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{article.read_time}</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
